import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PROBLEM_IMG =
    "https://images.pexels.com/photos/9107394/pexels-photo-9107394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SOLUTION_IMG =
    "https://images.pexels.com/photos/11368507/pexels-photo-11368507.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const reveal = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] },
};

function Chapter({ number, kicker, title, body, points, img, imgAlt, flip, grayscale, testId }) {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

    return (
        <div data-testid={testId} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
            <motion.div {...reveal} className={`lg:col-span-6 ${flip ? "lg:col-start-7" : ""}`}>
                <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">{kicker}</p>
                <div className="flex items-start gap-5">
                    <span className="font-display text-5xl leading-none text-warmgray md:text-6xl">{number}</span>
                    <h2 className="font-display text-3xl leading-tight tracking-tight md:text-4xl">{title}</h2>
                </div>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-navy">{body}</p>
                <ul className="mt-8 space-y-3">
                    {points.map((p) => (
                        <li key={p} className="flex items-center gap-3 text-sm font-medium text-navy">
                            <span className="block h-1.5 w-1.5 rotate-45 bg-gradient-to-br from-lavender to-peach" />
                            {p}
                        </li>
                    ))}
                </ul>
            </motion.div>
            <motion.div
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.15 }}
                className={`lg:col-span-5 ${flip ? "lg:col-start-1 lg:row-start-1" : "lg:col-start-8"}`}
            >
                <div ref={imgRef} className="relative overflow-hidden rounded-t-full border border-warmgray shadow-2xl shadow-navy/10">
                    <motion.img
                        style={{ y }}
                        src={img}
                        alt={imgAlt}
                        loading="lazy"
                        className="aspect-[4/5] w-full scale-110 object-cover"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/15 to-transparent" />
                </div>
            </motion.div>
        </div>
    );
}

export default function Chapters() {
    return (
        <section id="chapters" data-testid="chapters-section" className="mx-auto max-w-7xl space-y-32 px-6 py-28 md:py-36 lg:px-10">
            <Chapter
                testId="chapter-problem"
                number="01"
                kicker="Chapter 01 — The Problem"
                title="Your family’s story is slipping through the cracks."
                body="Photos are scattered across old phones and dusty albums. Voice notes vanish in expired group chats. And the stories your grandparents told at the dinner table fade a little more each year — until one day, nobody remembers them at all. And it’s more than photos: over generations, families lose voices, languages, and the connection to where they came from — especially when life scatters them across countries."
                points={[
                    "Memories fragmented across devices, apps, and shoeboxes",
                    "No single place the whole family can actually reach",
                    "Stories lost forever when a generation passes",
                    "Languages and family stories disappear between generations",
                ]}
                img={PROBLEM_IMG}
                imgAlt="Old family photographs and handwritten letters on a table"
                grayscale
            />
            <Chapter
                testId="chapter-solution"
                number="02"
                kicker="Chapter 02 — The Solution"
                title="One private timeline for every generation."
                body="Ago GenApp gathers your family’s photos, videos, voice recordings, written stories, and milestones into a single, beautifully organized timeline — invite-only, encrypted, and owned by your family. Not an algorithm’s. More than a family archive — a living connection between generations."
                points={[
                    "A living family timeline that grows with every generation",
                    "Private by design — invite-only access, no ads, no data selling",
                    "Built to be handed down, like the stories themselves",
                    "On the roadmap: voices preserved in their original language, with transcripts and translations so every generation understands",
                ]}
                img={SOLUTION_IMG}
                imgAlt="Grandmother sharing a photo album with her granddaughters on the sofa"
                flip
            />
        </section>
    );
}
